import type { RouteLocationNormalized } from 'vue-router';
import { LEGACY_STATE_QUERY_KEYS } from '../utils/funnelStateCode';
import {
  LAST_VISITED_PAGE_V2_COOKIE_KEY,
  type LastVisitedPageV2CookieModel,
} from '../utils/saveRoutesCookie';

interface QueriesModel {
  [key: string]: string
}

const LEGACY_STATE_QUERY_PATTERN = /^q\d+$/;

function withoutLegacyStateQueries<T extends Record<string, any>>(query: T): T {
  const cleaned = Object.fromEntries(
    Object.entries(query).filter(([key]) => {
      return !LEGACY_STATE_QUERY_KEYS.includes(key) && !LEGACY_STATE_QUERY_PATTERN.test(key);
    }),
  );

  return cleaned as T;
}

function isRouteExists(to: RouteLocationNormalized) {
  const routes = useRouter().getRoutes();
  return routes.some(r => (r.name && r.name === to.name) || r.path === to.path);
}

function stripLocalePrefix(path: string): string {
  const match = path.match(/^\/([A-Z]{2}(?:-[A-Z]{2})?)(\/.*)?$/i);
  return match ? (match[2] || '/') : '/';
}

function buildLocalizedPath(base: string, locale: string): string {
  if (base === '/' || base === '') {
    return `/${locale}`;
  }
  const clean = base.startsWith('/') ? base : `/${base}`;
  return `/${locale}${clean}`;
}

function getLocale(): string {
  const i18n = useNuxtApp().$i18n;
  return i18n?.locale?.value ?? 'en';
}

function getSavedRouteToRestore(
  savedRoute: LastVisitedPageV2CookieModel | null,
  currentFunnelName: string,
): { route: string, query: Record<string, any> } {
  if (savedRoute?.funnelName !== currentFunnelName || !savedRoute.lastRoute) {
    return { route: '', query: {} };
  }

  return {
    route: savedRoute.lastRoute,
    query: withoutLegacyStateQueries(savedRoute.query || {}),
  };
}

function isRootOfCurrentLocale(path: string, locale: string): boolean {
  return path === `/${locale}` || (path === '/' && locale === 'en');
}

function isRedirectFromExistRoute(from: RouteLocationNormalized, locale: string, prefix: string): boolean {
  const fromPath = stripFunnelPrefix(from.path, prefix);
  return fromPath !== '/' && fromPath !== `/${locale}` && isRouteExists(from);
}

function shouldRestoreRoute(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  locale: string,
  routeToRestore: string,
  prefix: string,
): boolean {
  return (
    isRootOfCurrentLocale(stripFunnelPrefix(to.path, prefix), locale)
    && !isRedirectFromExistRoute(from, locale, prefix)
    && Boolean(routeToRestore)
    && stripFunnelPrefix(routeToRestore, prefix) !== '/'
  );
}

function resolveRestoredFullPath(
  to: RouteLocationNormalized,
  router: ReturnType<typeof useRouter>,
  locale: string,
  routeToRestore: string,
  newQueries: QueriesModel,
  queriesToRestore: Record<string, any>,
  prefix: string,
): string | null {
  const lastBase = stripLocalePrefix(stripFunnelPrefix(routeToRestore, prefix));
  if (lastBase === '/bill' || stripFunnelPrefix(to.path, prefix) === '/bill') {
    return null;
  }

  const targetPath = `${prefix}${buildLocalizedPath(lastBase, locale)}`;
  const mergedQuery = { ...newQueries, ...queriesToRestore };
  const resolved = router.resolve({ path: targetPath, query: mergedQuery });

  return resolved.fullPath === to.fullPath ? null : resolved.fullPath;
}

function shouldMergeQueries(to: RouteLocationNormalized, newQueries: QueriesModel): boolean {
  return Object.keys(to.query || {}).length === 0 && Object.keys(newQueries).length > 0;
}

export default defineNuxtRouteMiddleware((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if (!isRouteExists(to)) {
    return;
  }

  const router = useRouter();
  const locale = getLocale();

  const newQueries = withoutLegacyStateQueries({ ...from.query, ...to.query }) as QueriesModel;
  const hasLegacyQueries = Object.keys(to.query || {}).length !== Object.keys(withoutLegacyStateQueries(to.query || {})).length;

  const cookiePageV2 = useCookie<LastVisitedPageV2CookieModel | null>(LAST_VISITED_PAGE_V2_COOKIE_KEY, { default: () => null, maxAge: 3600 });

  const currentFunnelName = useFunnelName(to.query.funnel);
  const funnelPrefix = useFunnelPrefix();

  const { route: routeToRestore, query: queriesToRestore } = getSavedRouteToRestore(
    cookiePageV2.value,
    currentFunnelName,
  );

  if (shouldRestoreRoute(to, from, locale, routeToRestore, funnelPrefix)) {
    const restoredFullPath = resolveRestoredFullPath(
      to,
      router,
      locale,
      routeToRestore,
      newQueries,
      queriesToRestore,
      funnelPrefix,
    );
    if (restoredFullPath) {
      return navigateTo(restoredFullPath, { replace: true });
    }
  }

  cookiePageV2.value = {
    funnelName: currentFunnelName,
    lastRoute: stripFunnelPrefix(to.path, funnelPrefix),
    query: newQueries,
  };

  if (shouldMergeQueries(to, newQueries) || hasLegacyQueries) {
    const resolved = router.resolve({ path: to.path, query: newQueries, hash: to.hash });
    if (resolved.fullPath !== to.fullPath) {
      return navigateTo(resolved.fullPath, { replace: true });
    }
  }
});
